import { Avatar, Checkbox, Input, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import { getUserOrganizationAccess } from '../../api/organization';
import { LogoFullIcon } from '../../assets/CustomIcons/LogoFullIcon';
import UnionBg from '../../assets/icons/union-bg.svg';
import { getCurrentlyActiveOrganization } from '../../common/auth';
import { BaseButton } from '../../components/UI/BaseButton';
import { queryClient } from '../../queryClient';
import { useGetDiscoverableWorkspaces, useGetWorkspaces, useJoinWorkspaceMutation } from '../../serverState/workspace';
import { getSpaceDefaultRoute } from '../../utils/workspaceUtils';

export default function JoinSpace() {
  const navigate = useNavigate();
  const [org, setOrg] = useState<{ id: number; publicId: string; name: string } | null>(null);
  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [isJoining, setIsJoining] = useState(false);

  const orgId = org?.id ?? null;
  const discoverableQuery = useGetDiscoverableWorkspaces(orgId);
  const workspacesQuery = useGetWorkspaces(orgId);
  const joinWorkspaceMutation = useJoinWorkspaceMutation();

  // Resolve the active org (async, from localforage) — mirrors NewSpace.
  useEffect(() => {
    getCurrentlyActiveOrganization()
      .then((o) => setOrg({ id: o.id, publicId: o.publicId, name: o.name }))
      .catch(() => navigate('/'));
  }, [navigate]);

  // Guards: never leave the user on a dead-end screen.
  useEffect(() => {
    if (!org) return;
    // Already has spaces (e.g. manual nav here) — go home.
    if (workspacesQuery.isSuccess && (workspacesQuery.data?.length ?? 0) > 0) {
      navigate('/');
      return;
    }
    // No spaces and nothing to discover — fall back to creating a space.
    const discoverableSettled = discoverableQuery.isSuccess || discoverableQuery.isError;
    if (workspacesQuery.isSuccess && discoverableSettled && (discoverableQuery.data?.length ?? 0) === 0) {
      navigate(`/${org.publicId}/new-space`);
    }
  }, [
    org,
    workspacesQuery.isSuccess,
    workspacesQuery.data,
    discoverableQuery.isSuccess,
    discoverableQuery.isError,
    discoverableQuery.data,
    navigate,
  ]);

  const spaces = discoverableQuery.data ?? [];

  const filteredSpaces = useMemo(() => {
    return spaces.filter((space) => space.name.toLowerCase().includes(searchValue.toLowerCase()));
  }, [spaces, searchValue]);

  const toggleSpace = (spaceId: number | string) => {
    setSelectedIds((prev) => (prev.includes(spaceId) ? prev.filter((id) => id !== spaceId) : [...prev, spaceId]));
  };

  const handleJoin = async () => {
    if (!org || selectedIds.length === 0) return;
    setIsJoining(true);
    try {
      const selectedSpaces = spaces.filter((space) => selectedIds.includes(space.id));
      const joinedSpaces = [];
      for (const space of selectedSpaces) {
        joinedSpaces.push(await joinWorkspaceMutation.mutateAsync({ spaceId: space.id, organizationId: org.id }));
        analytics.track(AnalyticsEvent.spaceJoined, { spaceId: space.id, organizationId: org.id });
      }
      // Prime the org-accesses cache before navigating into the space route. BaseIndex resolves the
      // space's org from the ['usersOrganizationAccess'] query and bounces to Select Organization if
      // it's missing; a freshly-invited user's cache is stale because invite acceptance only updates
      // localforage, not this query. Fetch directly and set the cache — refetchQueries won't work
      // here since the query has no active observer on this screen.
      const accesses = await getUserOrganizationAccess();
      queryClient.setQueryData(['usersOrganizationAccess'], accesses.data);
      const route = getSpaceDefaultRoute(org.publicId, joinedSpaces[0]);
      navigate(route ?? '/');
    } catch (e) {
      console.log(e);
      setIsJoining(false);
    }
  };

  const isLoading = !org || discoverableQuery.isPending;

  return (
    <div
      className="flex flex-col justify-center items-center gap-6 min-h-screen bg-brand-white"
      style={{
        backgroundImage: `url(${UnionBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="flex flex-col w-[656px] max-h-[80vh] bg-brand-white border-2 border-solid border-taskonioGreen-600 rounded-[12px] py-6 px-10 relative shadow-[0_0_0_4px_#7ff5aa,0_4px_16px_0_rgba(112,119,146,0.4)]">
        <div className="flex flex-col items-center justify-center gap-3 mb-8">
          <LogoFullIcon />
          <div className="text-brand-grey-dark text-base font-semibold">Welcome to {org?.name}</div>
        </div>

        <div className="text-brand-grey-dark text-xl font-semibold mb-3">Choose which Teamspaces to join</div>

        <Input
          placeholder="Search for Teamspace"
          prefix={<SearchOutlined className="mr-1" />}
          allowClear={searchValue.length > 0}
          className="[&_input::placeholder]:!text-neutral-800 text-neutral-900 mb-4"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 min-h-0 overflow-y-auto">
            {filteredSpaces.map((space) => (
              <div
                key={`discover-${space.id}`}
                className="flex items-center gap-2 px-3 py-2 rounded border border-solid border-neutral-300 cursor-pointer hover:border-taskonioGreen-600"
                onClick={() => toggleSpace(space.id)}
              >
                <Checkbox checked={selectedIds.includes(space.id)} />
                <Avatar className="flex-shrink-0 text-neutral-900 bg-brand-grey-light" shape="square" size={32}>
                  {space.name.substring(0, 1)}
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-brand-grey-dark text-sm font-medium">{space.name}</div>
                  <div className="text-neutral-900 text-xs">{space.memberCount} Members</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <BaseButton
            type="primary"
            size="large"
            disabled={selectedIds.length === 0 || isJoining}
            onClick={() => {
              void handleJoin();
            }}
          >
            {isJoining ? 'Joining…' : 'Join Teamspaces'}
          </BaseButton>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-neutral-900 text-sm">Or</span>
        <BaseButton type="primary" size="large" onClick={() => org && navigate(`/${org.publicId}/new-space`)}>
          Create a new Teamspace
        </BaseButton>
      </div>
    </div>
  );
}
